import { readStoredCompanyProfile } from "../../utils/companyProfileStorage"

// Universal search helper that can be reused across components
// - items: array of data to search
// - query: search string; empty or whitespace-only query returns original items
// - options:
//    - keys: object keys to search in; if omitted, item will be stringified
//    - caseSensitive: controls case sensitivity (default false)
//    - trim: trims query before search (default true)
//    - stringify: custom stringify function for items when keys not provided
export function universalSearch<T extends Record<string, any>>(
  items: T[],
  query: string | undefined | null,
  options?: {
    keys?: (keyof T)[]
    caseSensitive?: boolean
    trim?: boolean
    stringify?: (item: T) => string
  },
): T[] {
  if (!Array.isArray(items) || items.length === 0) return items

  const caseSensitive = options?.caseSensitive ?? false
  const trim = options?.trim ?? true
  let q = query ?? ""
  if (trim) q = q.trim()
  if (!caseSensitive) q = q.toLowerCase()
  if (q.length === 0) return items

  const keys = options?.keys
  const stringify: (item: T) => string =
    options?.stringify ??
    ((item: T) => {
      if (keys && keys.length > 0) {
        const values = keys
          .map((k) => item?.[k])
          .filter((v) => v !== undefined && v !== null)
        return values.join(" ")
      }
      // fallback: join all primitive values of the object
      return Object.values(item)
        .filter((v) => typeof v !== "object")
        .join(" ")
    })

  return items.filter((item) => {
    let hay = stringify(item)
    if (!caseSensitive) hay = hay.toLowerCase()
    return hay.includes(q)
  })
}

// Reusable print helper
// Allows printing arbitrary arrays by describing columns to render.
export type PrintColumn<T> = {
  header: string
  // Responsible for returning a safe string (already escaped if needed)
  // for the table cell. Typical usage: (row) => `${row.name ?? ""}`
  cell: (row: T, index: number) => string
  // Optional column width (CSS), e.g. "80px" or "20%"
  width?: string
}

export function printSelected<T>(
  items: T[],
  options: {
    title?: string
    addIndex?: boolean
    columns: PrintColumn<T>[]
    // Custom date to display in the header; defaults to now
    dateLabel?: string
    // Optional custom HTML block to render between the header and the table
    // Provide already-escaped content when inserting dynamic values
    headerHtml?: string
    // Optional custom HTML block to render on the right of the title
    // Provide already-escaped content when inserting dynamic values
    titleRightHtml?: string
    // Optional custom HTML block to render above the footer
    footerHtml?: string
    // Optional issuer name for signature block rendered above footer
    signatureIssuerName?: string
    companyProfile?: {
      shortName: string
      legalName: string
      address: {
        line1: string
        line2: string
      }
    }
  },
) {
  if (!Array.isArray(items) || items.length === 0) return
  const companyProfile = options.companyProfile ?? readStoredCompanyProfile()

  const title = options.title ?? `Wydruk pozycji (${items.length})`
  const addIndex = options.addIndex ?? false
  const cols = options.columns
  const headerHtml = options.headerHtml ?? ""
  const titleRightHtml = options.titleRightHtml ?? ""
  const footerHtml = options.footerHtml ?? ""
  const signatureIssuerName = options.signatureIssuerName ?? ""
  const signatureHtml = signatureIssuerName
    ? `
      <div style="display:flex; gap:24px; margin-top:18px; margin-bottom:12px;">
        <div style="flex:1;">
          <div style="font-size:11px; color:#555; margin-bottom:6px; text-align:center;">${escapeHtml(
            signatureIssuerName,
          )}</div>
          <div style="border-bottom:1px solid #999; height:22px;"></div>
          <div style="font-size:11px; color:#555; margin-top:6px; text-align:center;">Wystawił</div>
        </div>
        <div style="flex:1;">
          <div style="font-size:11px; color:#555; margin-bottom:6px; text-align:center;">&nbsp;</div>
          <div style="border-bottom:1px solid #999; height:22px;"></div>
          <div style="font-size:11px; color:#555; margin-top:6px; text-align:center;">Odebrał</div>
        </div>
      </div>`
    : ""

  const thead = `
      <tr>
        ${addIndex ? '<th style="width:40px">#</th>' : ""}
        ${cols
          .map(
            (c) =>
              `<th${c.width ? ` style="width:${c.width}"` : ""}>${
                c.header
              }</th>`,
          )
          .join("")}
      </tr>`

  const tbody = items
    .map((row, idx) => {
      const cells = cols.map((c) => `<td>${c.cell(row, idx)}</td>`).join("")
      return `<tr>${addIndex ? `<td>${idx + 1}</td>` : ""}${cells}</tr>`
    })
    .join("")

  const html = `<!doctype html>
        <html lang="pl">
          <head>
            <meta charset="utf-8" />
            <title>${escapeHtml(title)}</title>
            <style>
              @page { margin: 14mm 10mm 14mm 10mm; }
              body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 24px; color: #333; line-height: 1.28; counter-reset: page; }
              .print-shell { position: relative; }
              .print-header { margin-bottom: 10px; }
              .print-content { position: relative; }
              .header-main { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; border-bottom: 1px solid #333; padding-bottom: 6px; }
              .logo-container { font-weight: bold; font-size: 20px; color: #000; }
              .company-info { text-align: right; font-size: 9px; color: #666; line-height: 1.2; }
              h1 { font-size: 20px; margin: 0; color: #000; line-height: 1.1; }
              .meta { color: #666; font-size: 10px; margin-bottom: 10px; }
              .doc-info { background: #f9f9f9; padding: 10px 12px; border-radius: 4px; margin-bottom: 10px; border: 1px solid #eee; }
              .doc-info-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 6px 10px; font-size: 11px; }
              .doc-info-item { margin-bottom: 0; line-height: 1.2; }
              .doc-info-label { font-weight: bold; color: #555; margin-right: 5px; }
              .title-row { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 6px; }
              .title-right { text-align: center; }
              .title-right svg { max-width: 180px; height: 38px; }
              .title-right-text { font-size: 9px; letter-spacing: 1px; margin-top: 2px; color: #555; }
              table { width: 100%; border-collapse: collapse; margin-top: 6px; }
              th, td { border: 1px solid #ddd; padding: 7px 8px; font-size: 11px; text-align: left; line-height: 1.2; }
              th { background: #f5f5f5; font-weight: bold; color: #333; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; }
              tr:nth-child(even) { background-color: #fafafa; }
              thead { display: table-header-group; }
              tr { page-break-inside: avoid; }
              .page-footer { display: none; }
              .page-number::after { content: counter(page); }
              .footer { margin-top: 18px; font-size: 9px; color: #999; text-align: center; border-top: 1px solid #eee; padding-top: 8px; }
              @media print {
                body { margin: 0; }
                .no-print { display: none; }
                .doc-info { background: #fff; border: 1px solid #ccc; }
                .print-header {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  padding: 6mm 10mm 2mm 10mm;
                  background: #fff;
                }
                .print-content {
                  margin-top: 100mm;
                  margin-bottom: 18mm;
                }
                .page-footer {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  position: fixed;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  padding: 4mm 10mm 6mm 10mm;
                  background: #fff;
                  border-top: 1px solid #eee;
                  font-size: 9px;
                  color: #888;
                }
                .footer {
                  display: none;
                }
              }
            </style>
          </head>
          <body>
            <div class="no-print" style="text-align:right;margin-bottom:12px;">
              <button onclick="window.print()" style="padding: 8px 16px; cursor: pointer;">Drukuj dokument</button>
            </div>
            <div class="print-shell">
              <div class="print-header">
                <div class="header-main">
                  <div class="logo-container">${escapeHtml(companyProfile.shortName)}</div>
                  <div class="company-info">
                    ${escapeHtml(companyProfile.legalName)}<br>
                    ${escapeHtml(companyProfile.address.line1)}<br>
                    ${escapeHtml(companyProfile.address.line2)}
                  </div>
                </div>

                <div class="title-row">
                  <h1>${escapeHtml(title)}</h1>
                  ${
                    titleRightHtml
                      ? `<div class="title-right">${titleRightHtml}</div>`
                      : ""
                  }
                </div>
                <div class="meta">Data wydruku: ${escapeHtml(
                  new Date().toLocaleString(),
                )}</div>
                
                <div class="doc-info">
                  ${headerHtml}
                </div>
              </div>

              <div class="print-content">
                <table>
                  <thead>
                    ${thead}
                  </thead>
                  <tbody>
                    ${tbody}
                  </tbody>
                </table>

                ${signatureHtml}
                ${footerHtml}

                <div class="footer">
                  Stockerra - Dokument wygenerowany automatycznie
                </div>
              </div>

              <div class="page-footer">
                <div>Stockerra - Dokument wygenerowany automatycznie</div>
                <div>Strona <span class="page-number"></span></div>
              </div>
            </div>

            <script>window.onload = function(){ window.focus(); };</script>
          </body>
        </html>`

  const w = window.open("", "_blank")
  if (w) {
    w.document.open()
    w.document.write(html)
    w.document.close()
  }
}

// Simple HTML escaper for text content
export function escapeHtml(input: string) {
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
}
